// ABOUTME: Authentication guard that follows blab-auth's session states and applies the landing-page preference
// ABOUTME: Signed-out users are sent to hosted sign-in; a 401 the API could not recover triggers a token refresh

import type { BrowserAuth } from '@boundarylab/auth/browser';
import { Alert, Button, Spinner } from '@boundarylab/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePreferences } from '../hooks/usePreferences';
import { auth as defaultAuth, SKIP_AUTH } from '../lib/auth-client';
import { AccessDenied } from './AccessDenied';

const SESSION_QUERY_KEY = ['auth-session'];

function LandingRedirect({ auth }: { auth: BrowserAuth }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { isLoading, isError, defaultLandingPath } = usePreferences(auth);
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current || isLoading || isError) return;
    handled.current = true;
    if (pathname === '/' && defaultLandingPath !== '/') {
      navigate(defaultLandingPath, { replace: true });
    }
  }, [isLoading, isError, pathname, defaultLandingPath, navigate]);

  return null;
}

function FullScreenSpinner() {
  return (
    <div className="flex h-screen items-center justify-center bg-slate-50">
      <Spinner />
    </div>
  );
}

export function AuthGate({
  children,
  auth = defaultAuth,
}: {
  children: React.ReactNode;
  auth?: BrowserAuth;
}) {
  const queryClient = useQueryClient();
  const { status } = useAuth(auth, SKIP_AUTH);

  useEffect(() => {
    if (SKIP_AUTH) return;
    // Several in-flight API calls can 401 in the same tick. The flag debounces
    // refresh so we only re-validate the session once per burst.
    let refreshing = false;
    function onUnauthorized() {
      if (refreshing) return;
      refreshing = true;
      void auth
        .refresh()
        .then((result) => {
          if (result.status !== 'unavailable') queryClient.setQueryData(SESSION_QUERY_KEY, result);
        })
        .finally(() => {
          refreshing = false;
        });
    }
    window.addEventListener('auth:unauthorized', onUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', onUnauthorized);
  }, [auth, queryClient]);

  useEffect(() => {
    if (status === 'signed_out') auth.redirectToLogin();
  }, [status, auth]);

  if (SKIP_AUTH) return <>{children}</>;

  switch (status) {
    case 'signed_in':
      return (
        <>
          <LandingRedirect auth={auth} />
          {children}
        </>
      );
    case 'not_provisioned':
      return <AccessDenied auth={auth} />;
    case 'unavailable':
      return (
        <div className="flex h-screen items-center justify-center bg-slate-50">
          <Alert variant="warning" title="Sign-in service unreachable">
            <Button
              size="sm"
              variant="outline"
              onClick={() => void queryClient.refetchQueries({ queryKey: SESSION_QUERY_KEY })}
            >
              Retry
            </Button>
          </Alert>
        </div>
      );
    default:
      return <FullScreenSpinner />;
  }
}
